import { useState, useEffect, useRef } from 'react';
import GreenTick from '@/assets/Icons/components/check-box/GreenTick';
import CustomMultipleFileUpload from './CustomMultipleFileUpload';
import { setHasUnsavedChanges } from '@/reducers/formSlice';
import { useDispatch } from 'react-redux';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { trackEvent } from '@/utils/track-event';
import { PostHogMessages } from '@/utils/PostHogMessages';
import UserCustomerTextArea from './UserCustomerTextArea';
import { getAuthData } from '@/utils/auth';

interface FieldOption {
  label: string;
  value: string;
  fields: NestedField[];
}

interface NestedField {
  field_id: string;
  order: number;
  label: string;
  field_type: string;
  tabName: string;
  is_required: boolean;
  value: string;
  values: any[];
  files?: any[];
  field_scope: string;
  table_responses: any[];
  additional_config?: {
    helpText?: string;
    placeholder?: string;
    editable?: boolean;
  };
  is_admin_review?: boolean;
  highlight?: any;
}
interface CustomMultipleFieldProps {
  name?: string;
  fieldId: string;
  label?: string;
  options?: FieldOption[];
  value?: string;
  defaultValue?: string;
  values?: any[];
  onChange?: (fieldId: string, value: any) => void;
  isRequired?: boolean;
  description?: string;
  isLockedByMe?: boolean;
  lockedOwner?: string;
  isReviewForm?: boolean;
  sectionData?: any;
  helpText?: string;
  onFocus?: () => void;
  isPdf?: boolean;
  isCancel?: boolean;
  setIsCancel?: any;
  className?: string;
  field?: any;
  highlight?: boolean;
  shouldHighlightNestedField?: (nestedField: any) => boolean;
  shouldHighlightOption?: (option: any) => boolean;
  shouldHighlightFieldInOption?: (nestedField: any) => boolean;
}

const CustomMultipleField: React.FC<CustomMultipleFieldProps> = ({
  label,
  options = [],
  value,
  defaultValue,
  onChange,
  isRequired = false,
  isLockedByMe = false,
  lockedOwner = '',
  isReviewForm = false,
  sectionData,
  helpText,
  fieldId,
  onFocus,
  isPdf,
  isCancel,
  setIsCancel,
  className,
  highlight,
  shouldHighlightOption,
  shouldHighlightNestedField,
  // shouldHighlightFieldInOption,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(
    value || defaultValue || null
  );
  const [nestedFieldValues, setNestedFieldValues] = useState<Record<string, any>>({});
  const [allOptionsData, setAllOptionsData] = useState<Record<string, Record<string, any>>>({});

  const { role } = getAuthData();

  const originalValues = useRef({
    selectedOption: value || defaultValue || null,
    nestedFieldValues: {} as Record<string, any>,
  });

  const conditionFieldId = fieldId;
  const prevSectionDataRef = useRef<any>(null);
  const dispatch = useDispatch();
  const prevSelectedOptionRef = useRef<string | null>(null);

  // Handle cancel - revert to original values
  useEffect(() => {
    if (isCancel) {
      setSelectedOption(originalValues.current.selectedOption);
      setNestedFieldValues(originalValues.current.nestedFieldValues);
      setIsCancel(false);
    }
  }, [isCancel]);

  // Initialize state when sectionData changes
  useEffect(() => {
    if (prevSectionDataRef.current !== sectionData) {
      const conditionValue = value || defaultValue || null;
      setSelectedOption(conditionValue);
      originalValues.current.selectedOption = conditionValue;

      // Initialize nested field values
      const initialNestedValues: Record<string, any> = {};
      const initialAllOptionsData: Record<string, Record<string, any>> = {};

      options.forEach((option) => {
        initialAllOptionsData[option.value] = {};

        option.fields.forEach((field: NestedField) => {
          if (field.field_type === 'file') {
            initialAllOptionsData[option.value][field.field_id] = {
              files: field.files || [],
              fileName: field.value || '',
              fileSize: field.values?.[1] || null,
            };
          } else {
            initialAllOptionsData[option.value][field.field_id] = field.value || '';
          }
        });
      });

      if (conditionValue) {
        const selectedOption = options.find((opt) => opt.value === conditionValue);
        if (selectedOption) {
          selectedOption.fields.forEach((field: NestedField) => {
            if (field.field_type === 'file') {
              initialNestedValues[field.field_id] = {
                files: field.files || [],
                fileName: field.value || '',
                fileSize: field.values?.[1] || null,
              };
            } else {
              initialNestedValues[field.field_id] = field.value || '';
            }
          });
        }
      }

      setNestedFieldValues(initialNestedValues);
      setAllOptionsData(initialAllOptionsData);
      originalValues.current.nestedFieldValues = initialNestedValues;
      prevSectionDataRef.current = sectionData;
      prevSelectedOptionRef.current = conditionValue;

      // Notify parent of initial state
      if (onChange && conditionValue) {
        onChange(conditionFieldId, {
          fields: [
            {
              field_id: conditionFieldId,
              value: conditionValue,
              field_scope: 'condition',
              nested_fields: getNestedFieldsPayload(conditionValue, initialNestedValues),
              type: 'condition_with_multiple_fields',
            },
          ],
        });
      }
    }
  }, [sectionData, value, defaultValue, onChange, conditionFieldId, options]);

  // When option changes, update nestedFieldValues with stored data for that option
  useEffect(() => {
    if (selectedOption !== null && selectedOption !== prevSelectedOptionRef.current) {
      // Save current option's data before switching
      if (prevSelectedOptionRef.current !== null) {
        setAllOptionsData((prev) => ({
          ...prev,
          [prevSelectedOptionRef.current as string]: nestedFieldValues,
        }));
      }

      // Load new option's data
      setNestedFieldValues(allOptionsData[selectedOption] || {});
      prevSelectedOptionRef.current = selectedOption;
    }
  }, [selectedOption, allOptionsData]);

  // Helper function to create nested fields payload
  const getNestedFieldsPayload = (optionValue: string, values: Record<string, any>) => {
    const selectedOptionObj = options.find((opt) => opt.value === optionValue);
    if (!selectedOptionObj) return [];

    return selectedOptionObj.fields
      .map((field: any) => {
        const currentValue = values[field.field_id] ?? field.value;
        if (!currentValue && currentValue !== '') return null;

        if (field.field_type === 'file') {
          if (typeof currentValue === 'object' && currentValue !== null) {
            return {
              field_id: field.field_id,
              files: currentValue.files,
              value: currentValue.fileName || '',
              values: [String(currentValue.fileName || ''), currentValue.fileSize ?? null],
            };
          } else if (typeof currentValue === 'string') {
            return {
              field_id: field.field_id,
              value: currentValue,
              values: field.values || [],
            };
          }
          return null;
        } else {
          return {
            field_id: field.field_id,
            value: currentValue,
          };
        }
      })
      .filter(Boolean);
  };

  const handleOptionSelect = (optionValue: string) => {
    // Save current option's data before switching
    if (selectedOption !== null) {
      setAllOptionsData((prev) => ({
        ...prev,
        [selectedOption]: nestedFieldValues,
      }));
    }

    setSelectedOption(optionValue);
    dispatch(setHasUnsavedChanges(true));

    if (onChange) {
      onChange(conditionFieldId, {
        fields: [
          {
            field_id: conditionFieldId,
            value: optionValue,
            field_scope: 'condition',
            nested_fields: getNestedFieldsPayload(optionValue, allOptionsData[optionValue] || {}),
            type: 'condition_with_multiple_fields',
          },
        ],
      });
    }
  };

  const handleNestedFieldChange = (fieldId: string, fieldValue: any) => {
    const newNestedValues = {
      ...nestedFieldValues,
      [fieldId]: fieldValue,
    };
    setNestedFieldValues(newNestedValues);
    dispatch(setHasUnsavedChanges(true));

    // Update the stored data for the current option
    if (selectedOption) {
      setAllOptionsData((prev) => ({
        ...prev,
        [selectedOption]: {
          ...prev[selectedOption],
          [fieldId]: fieldValue,
        },
      }));
    }

    if (onChange && selectedOption) {
      onChange(conditionFieldId, {
        fields: [
          {
            field_id: conditionFieldId,
            value: selectedOption,
            field_scope: 'condition',
            nested_fields: getNestedFieldsPayload(selectedOption, newNestedValues),
            type: 'condition_with_multiple_fields',
          },
        ],
      });
    }
  };

  const getNestedFieldValue = (fieldId: string, defaultValue: any) => {
    return nestedFieldValues[fieldId] !== undefined ? nestedFieldValues[fieldId] : defaultValue;
  };

  const renderNestedField = (nestedField: NestedField) => {
    const nestedFieldValue = getNestedFieldValue(nestedField.field_id, nestedField.value);

    // Use the highlighting function if provided, otherwise fall back to the old logic
    const shouldHighlightThisField = shouldHighlightNestedField
      ? shouldHighlightNestedField(nestedField)
      : nestedField.is_admin_review && role === 'user' && highlight;

    const highlightClass = shouldHighlightThisField ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6]' : '';

    console.log('Rendering nested field:', {
      fieldId: nestedField.field_id,
      isAdminReview: nestedField.is_admin_review,
      shouldHighlight: shouldHighlightThisField,
      highlightFunction: !!shouldHighlightNestedField,
    });

    const handleFocus = () => {
      trackEvent(PostHogEvents.FORM_FIELD_FOCUSED, {
        field_name: nestedField.label,
        form_id: nestedField.field_id,
        message: PostHogMessages[PostHogEvents.FORM_FIELD_FOCUSED],
      });
    };

    switch (nestedField.field_type) {
      case 'text_area':
        return (
          <>
            <UserCustomerTextArea
              key={nestedField.field_id}
              name={nestedField.field_id}
              value={nestedFieldValue}
              label={'Description'}
              onChange={(e) => handleNestedFieldChange(nestedField.field_id, e.target.value)}
              placeholder={nestedField.additional_config?.placeholder || ''}
              required={nestedField.is_required}
              description={nestedField?.additional_config?.helpText}
              isCondition={true}
              className={`w-full ${highlightClass}`}
              rows={8}
              isLockedByMe={isLockedByMe}
              lockedOwner={lockedOwner}
              isReviewForm={isReviewForm}
              onFocus={handleFocus}
              isPdf={isPdf}
            />
            {selectedOption === 'yes' &&
              sectionData?.data?.name === 'Incidence of Other Traumatic Response' && (
                <div className="bg-ds-orange-100 text-ds-orange-900 p-4 rounded mt-4 mb-4 text-xs">
                  <b>Note:</b> Gov. Abbott declared all Texas public schools to close for in-person
                  instruction on March 19, 2020. Schools were required to provide in-person
                  instruction during the 2020-21 school year. Please generally describe your
                  school&apos;s Covid operations and provide the requested documents.
                </div>
              )}
          </>
        );
      case 'file':
        return (
          <CustomMultipleFileUpload
            value={nestedFieldValue}
            name={nestedField.field_id}
            onChange={(fileData) => handleNestedFieldChange(nestedField.field_id, fileData)}
            required={nestedField.is_required}
            isCondition={true}
            isLockedByMe={isLockedByMe}
            lockedOwner={lockedOwner}
            isReviewForm={isReviewForm}
            values={nestedField?.files}
            onFocus={handleFocus}
            isCancel={isCancel}
            isPdf={isPdf}
            className={`${highlightClass}`}
          />
        );
      default:
        return null;
    }
  };

  const selectedOptionData = options.find((opt) => opt.value === selectedOption);

  return (
    <div className="w-full">
      <div className="mb-2 border-b py-4">
        <label className="block font-semibold text-ds-gray-800 ">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
        <p className="text-xs text-ds-gray-400 mt-1 confirmation-note">{helpText}</p>
      </div>

      <div className={`flex items-center space-x-2 mt-2 ${className}`}>
        {options.map((option) => {
          // Check if this option should be highlighted
          const shouldHighlightThisOption = shouldHighlightOption
            ? shouldHighlightOption(option)
            : false;

          // const optionHighlightClass = shouldHighlightThisOption
          //   ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6] p-2 rounded'
          //   : '';

          console.log('Rendering option:', {
            value: option.value,
            shouldHighlight: shouldHighlightThisOption,
            highlightFunction: !!shouldHighlightOption,
            option: option,
          });

          return (
            <div key={option.value} className={`flex items-center mr-4`}>
              <button
                className={`flex items-center justify-center rounded-full w-4 h-4 ${isPdf ? 'mt-4' : ''} ${
                  isReviewForm ? 'cursor-pointer' : 'cursor-default'
                } ${
                  selectedOption === option.value
                    ? 'bg-ds-brand-mc-green-2 text-white'
                    : 'border border-gray-300'
                }`}
                onClick={() => {
                  handleOptionSelect(option.value);
                  onFocus?.();
                }}
                aria-pressed={selectedOption === option.value}
                role="radio"
                type="button"
                disabled={isReviewForm || (lockedOwner !== '' && !isLockedByMe)}
              >
                {selectedOption === option.value && <GreenTick />}
              </button>
              <span className="text-sm ml-2 text-gray-500">{option.label}</span>
            </div>
          );
        })}
      </div>

      {selectedOptionData?.fields?.map(renderNestedField)}

      {selectedOption === 'no' &&
        sectionData?.data?.name === 'Incidence of Other Traumatic Response' && (
          <div className="bg-ds-orange-100 text-ds-orange-900 p-4 rounded mt-10 text-xs">
            <b>Note:</b> GOV. ABBOTT DECLARED ALL TEXAS PUBLIC SCHOOLS TO CLOSE FOR IN-PERSON
            INSTRUCTION ON MARCH 19, 2020. THIS APPLIED TO ALL PUBLC AND PRIVATE SCHOOLS. PLEASE
            REACH OUT TO PFS.QUESTIONS@MOAKCASEY.COM IF YOU BELIEVE YOUR DISTRICT DID NOT CLOSE
            DURING THIS TIME.
          </div>
        )}
    </div>
  );
};

export default CustomMultipleField;
